import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { DatabaseContext, UserContext } from "../../context";
import firebase from "../../firebase";
import { downloadBlob } from "../../utility";
import ToolbarWithLogo from "../ToolbarWithLogo";

const surfaceSx = {
    borderRadius: 3,
    borderColor: "divider",
    boxShadow: "0 7px 24px rgba(20, 35, 25, 0.055)",
};

function formatAnswerText(answer) {
    if (answer === null || answer === undefined || answer === "") {
        return "";
    }

    return String(answer).replace(/£\$%/g, ", ");
}

function formatSubmissionDate(value) {
    const date = dayjs(value);
    return date.isValid()
        ? date.format("D MMM YYYY, HH:mm")
        : "Date unavailable";
}

function mapResponses(snapshot, surveyKey) {
    const responses = [];

    snapshot.forEach((child) => {
        const data = child.val();
        if (data?.survey !== surveyKey) {
            return;
        }

        responses.push({
            key: child.key,
            submissionDate: data.submissionDate,
            user: data.user,
            answers: Object.entries(data.answers || {}).map(
                ([key, answer]) => ({
                    key,
                    answer: answer.answer ?? "",
                    question: answer.question ?? "",
                }),
            ),
        });
    });

    return responses;
}

export default function SurveyResultsPage() {
    const { currentUserData } = useContext(UserContext);
    const {
        groups = [],
        groupsLoaded = false,
        surveyInfo = [],
        surveyInfoLoaded = false,
        users = [],
    } = useContext(DatabaseContext);
    const history = useHistory();
    const location = useLocation();

    const [responses, setResponses] = useState([]);
    const [columns, setColumns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [sortBy, setSortBy] = useState({
        key: "submissionDate",
        direction: "desc",
    });
    const [filterValues, setFilterValues] = useState(new Map());
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [filterColumn, setFilterColumn] = useState(null);
    const [dialogValues, setDialogValues] = useState([]);
    const [dialogSelectedValues, setDialogSelectedValues] = useState(
        new Set(),
    );

    const surveyKey = useMemo(
        () => new URLSearchParams(location.search).get("key"),
        [location.search],
    );
    const surveyDetails = useMemo(
        () => surveyInfo.find((survey) => survey.key === surveyKey),
        [surveyInfo, surveyKey],
    );

    const userHasAccess = useCallback(
        (survey) => {
            if (!survey || !currentUserData) {
                return false;
            }
            if (survey.createdBy === currentUserData.key) {
                return true;
            }
            if (
                (survey.users || []).some(
                    (user) =>
                        user.key === currentUserData.key &&
                        user.canViewResults,
                )
            ) {
                return true;
            }

            return (survey.groups || []).some((surveyGroup) => {
                const matchingGroup = groups.find(
                    (group) => group.key === surveyGroup.key,
                );
                return (
                    surveyGroup.canViewResults &&
                    (matchingGroup?.users || []).some(
                        (user) => user.key === currentUserData.key,
                    )
                );
            });
        },
        [currentUserData, groups],
    );

    useEffect(() => {
        let isCurrent = true;

        if (!currentUserData || !surveyInfoLoaded || !groupsLoaded) {
            setIsLoading(true);
            return () => {
                isCurrent = false;
            };
        }

        if (!surveyKey || !surveyDetails) {
            setLoadError(
                "This survey is unavailable or may have been deleted.",
            );
            setIsLoading(false);
            return () => {
                isCurrent = false;
            };
        }

        if (!userHasAccess(surveyDetails)) {
            setLoadError(
                "You do not have permission to view this survey’s results.",
            );
            setIsLoading(false);
            return () => {
                isCurrent = false;
            };
        }

        setIsLoading(true);
        setLoadError("");
        setResponses([]);
        setColumns([]);
        setFilterValues(new Map());

        Promise.all([
            firebase
                .database()
                .ref("surveys")
                .child(surveyKey)
                .once("value"),
            firebase
                .database()
                .ref("surveyresponses")
                .once("value"),
        ])
            .then(([surveySnapshot, responsesSnapshot]) => {
                if (!isCurrent) {
                    return;
                }

                const questionColumns = Object.entries(
                    surveySnapshot.val()?.questions || {},
                )
                    .map(([key, question]) => ({
                        key,
                        row: question.row ?? 0,
                        text:
                            question.questionText ||
                            "Untitled question",
                        answerFormat: question.answerFormat,
                    }))
                    .filter(
                        (question) =>
                            question.answerFormat !== "File",
                    )
                    .sort((first, second) => first.row - second.row);

                setColumns([
                    { key: "user", text: "Respondent", fixed: true },
                    {
                        key: "submissionDate",
                        text: "Submitted",
                        fixed: true,
                    },
                    ...questionColumns,
                ]);
                setResponses(
                    mapResponses(responsesSnapshot, surveyKey),
                );
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Unable to load survey results", error);
                if (isCurrent) {
                    setLoadError(
                        "The results could not be loaded. Please check your connection and try again.",
                    );
                    setIsLoading(false);
                }
            });

        return () => {
            isCurrent = false;
        };
    }, [
        currentUserData,
        groupsLoaded,
        surveyDetails,
        surveyInfoLoaded,
        surveyKey,
        userHasAccess,
    ]);

    const rows = useMemo(() => {
        const questionColumns = columns.slice(2);

        return responses.map((response) => {
            const matchingUser = users.find(
                (user) => user.key === response.user,
            );

            return {
                key: response.key,
                user: matchingUser
                    ? matchingUser.fullName
                    : response.user === "Anonymous"
                    ? "Anonymous"
                    : "Unknown user",
                submissionDate: response.submissionDate,
                answers: questionColumns.map((column) => {
                    const matchingAnswer = response.answers.find(
                        (answer) =>
                            answer.question === column.key ||
                            answer.key === column.key,
                    );
                    return matchingAnswer
                        ? formatAnswerText(matchingAnswer.answer)
                        : "";
                }),
            };
        });
    }, [columns, responses, users]);

    const displayedRows = useMemo(() => {
        const questionColumns = columns.slice(2);
        const filteredRows = rows.filter((row) =>
            questionColumns.every((column, index) => {
                if (!filterValues.has(column.key)) {
                    return true;
                }
                return filterValues
                    .get(column.key)
                    .has(row.answers[index] ?? "");
            }),
        );
        const multiplier = sortBy.direction === "asc" ? 1 : -1;

        return [...filteredRows].sort((first, second) => {
            if (sortBy.key === "submissionDate") {
                return (
                    (new Date(first.submissionDate) -
                        new Date(second.submissionDate)) *
                    multiplier
                );
            }
            if (sortBy.key === "user") {
                return (
                    first.user.localeCompare(second.user) * multiplier
                );
            }

            const questionIndex =
                columns.findIndex(
                    (column) => column.key === sortBy.key,
                ) - 2;
            if (questionIndex >= 0) {
                return (
                    String(
                        first.answers[questionIndex] ?? "",
                    ).localeCompare(
                        String(second.answers[questionIndex] ?? ""),
                    ) * multiplier
                );
            }
            return 0;
        });
    }, [columns, filterValues, rows, sortBy]);

    function handleSort(columnKey) {
        setSortBy((current) => ({
            key: columnKey,
            direction:
                current.key === columnKey &&
                current.direction === "asc"
                    ? "desc"
                    : columnKey === "submissionDate"
                    ? "desc"
                    : "asc",
        }));
    }

    function openFilterDialog(column) {
        const questionIndex =
            columns.findIndex((item) => item.key === column.key) - 2;
        const uniqueValues = Array.from(
            new Set(
                rows.map(
                    (row) => row.answers[questionIndex] ?? "",
                ),
            ),
        ).sort((first, second) =>
            String(first).localeCompare(String(second)),
        );

        setFilterColumn(column);
        setDialogValues(uniqueValues);
        setDialogSelectedValues(
            new Set(
                filterValues.has(column.key)
                    ? filterValues.get(column.key)
                    : uniqueValues,
            ),
        );
        setFilterDialogOpen(true);
    }

    function toggleDialogValue(value) {
        setDialogSelectedValues((current) => {
            const next = new Set(current);
            if (next.has(value)) {
                next.delete(value);
            } else {
                next.add(value);
            }
            return next;
        });
    }

    function applyFilter() {
        if (!filterColumn) {
            return;
        }

        setFilterValues((current) => {
            const next = new Map(current);
            if (dialogSelectedValues.size === dialogValues.length) {
                next.delete(filterColumn.key);
            } else {
                next.set(
                    filterColumn.key,
                    new Set(dialogSelectedValues),
                );
            }
            return next;
        });
        setFilterDialogOpen(false);
    }

    function clearCurrentFilter() {
        if (filterColumn) {
            setFilterValues((current) => {
                const next = new Map(current);
                next.delete(filterColumn.key);
                return next;
            });
        }
        setFilterDialogOpen(false);
    }

    function downloadCSV() {
        if (columns.length === 0) {
            return;
        }

        const csvRows = [
            columns.map((column) => column.text),
            ...displayedRows.map((row) => [
                row.user,
                row.submissionDate,
                ...row.answers,
            ]),
        ];
        const csv = csvRows
            .map((row) =>
                row
                    .map(
                        (cell) =>
                            `"${String(cell ?? "").replace(
                                /"/g,
                                '""',
                            )}"`,
                    )
                    .join(","),
            )
            .join("\n");
        const safeTitle = (
            surveyDetails?.title || "survey"
        )
            .replace(/[^a-z0-9]+/gi, "-")
            .replace(/^-|-$/g, "")
            .toLowerCase();

        downloadBlob(
            csv,
            `${safeTitle || "survey"}-responses.csv`,
            "text/csv;charset=utf-8;",
        );
    }

    const activeFilterCount = filterValues.size;
    const tableMinWidth = Math.max(720, columns.length * 230);

    return (
        <>
            <Box
                sx={{
                    minHeight: {
                        xs: "calc(100dvh - 60px)",
                        sm: "calc(100vh - 128px)",
                    },
                    pb: 12,
                    backgroundColor: "background.default",
                }}
            >
                <Container
                    maxWidth="xl"
                    sx={{
                        px: { xs: 1.5, sm: 3 },
                        py: { xs: 2, sm: 3 },
                    }}
                >
                    <Button
                        color="inherit"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => history.goBack()}
                        sx={{ mb: 2 }}
                    >
                        Back to surveys
                    </Button>

                    {isLoading ? (
                        <Card variant="outlined" sx={surfaceSx}>
                            <CardContent
                                sx={{
                                    minHeight: 420,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "text.secondary",
                                }}
                            >
                                <CircularProgress
                                    size={34}
                                    aria-label="Loading survey results"
                                />
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        mt: 1.5,
                                        fontWeight: 800,
                                        color: "text.primary",
                                    }}
                                >
                                    Loading results
                                </Typography>
                                <Typography variant="body2">
                                    Compiling survey responses…
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : loadError ? (
                        <Card variant="outlined" sx={surfaceSx}>
                            <CardContent
                                sx={{
                                    minHeight: 340,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 3,
                                    textAlign: "center",
                                }}
                            >
                                <ErrorOutlineIcon
                                    color="error"
                                    sx={{ mb: 1.25, fontSize: 48 }}
                                />
                                <Typography variant="h6">
                                    Results unavailable
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.75, maxWidth: 520 }}
                                >
                                    {loadError}
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : (
                        <Box sx={{ display: "grid", gap: 2 }}>
                            <Card variant="outlined" sx={surfaceSx}>
                                <CardContent
                                    sx={{
                                        p: { xs: 2.5, sm: 3 },
                                        display: "flex",
                                        alignItems: {
                                            xs: "flex-start",
                                            md: "center",
                                        },
                                        flexDirection: {
                                            xs: "column",
                                            md: "row",
                                        },
                                        gap: 2,
                                        "&:last-child": {
                                            pb: { xs: 2.5, sm: 3 },
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1.5,
                                            flex: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 46,
                                                height: 46,
                                                display: "grid",
                                                placeItems: "center",
                                                flexShrink: 0,
                                                borderRadius: 2,
                                                color: "primary.main",
                                                backgroundColor:
                                                    "rgba(0, 149, 61, 0.09)",
                                            }}
                                        >
                                            <BarChartOutlinedIcon />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                variant="h5"
                                                component="h1"
                                                sx={{ lineHeight: 1.1 }}
                                            >
                                                Survey results
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mt: 0.5,
                                                    overflowWrap:
                                                        "anywhere",
                                                }}
                                            >
                                                {surveyDetails.title ||
                                                    "Untitled survey"}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            width: {
                                                xs: "100%",
                                                md: "auto",
                                            },
                                            display: "flex",
                                            alignItems: "center",
                                            flexWrap: "wrap",
                                            gap: 1,
                                        }}
                                    >
                                        <Chip
                                            label={`${rows.length} ${
                                                rows.length === 1
                                                    ? "response"
                                                    : "responses"
                                            }`}
                                        />
                                        {activeFilterCount > 0 && (
                                            <Chip
                                                color="primary"
                                                variant="outlined"
                                                label={`${activeFilterCount} ${
                                                    activeFilterCount === 1
                                                        ? "filter"
                                                        : "filters"
                                                }`}
                                                onDelete={() =>
                                                    setFilterValues(
                                                        new Map(),
                                                    )
                                                }
                                            />
                                        )}
                                        <Button
                                            variant="outlined"
                                            startIcon={
                                                <DownloadOutlinedIcon />
                                            }
                                            onClick={downloadCSV}
                                            disabled={rows.length === 0}
                                        >
                                            Download CSV
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>

                            <Card
                                variant="outlined"
                                sx={{
                                    ...surfaceSx,
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: {
                                            xs: "flex-start",
                                            sm: "center",
                                        },
                                        justifyContent:
                                            "space-between",
                                        flexDirection: {
                                            xs: "column",
                                            sm: "row",
                                        },
                                        gap: 1,
                                        px: { xs: 1.5, sm: 2 },
                                        py: 1.5,
                                        borderBottom: "1px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{ fontWeight: 800 }}
                                        >
                                            Responses
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {activeFilterCount > 0
                                                ? `Showing ${displayedRows.length} of ${rows.length} responses`
                                                : "Sort columns or filter individual answers."}
                                        </Typography>
                                    </Box>
                                    {activeFilterCount > 0 && (
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={() =>
                                                setFilterValues(
                                                    new Map(),
                                                )
                                            }
                                        >
                                            Clear filters
                                        </Button>
                                    )}
                                </Box>

                                {rows.length === 0 ? (
                                    <Box
                                        sx={{
                                            minHeight: 300,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            px: 3,
                                            textAlign: "center",
                                        }}
                                    >
                                        <InboxOutlinedIcon
                                            sx={{
                                                mb: 1,
                                                fontSize: 46,
                                                color: "text.disabled",
                                            }}
                                        />
                                        <Typography variant="h6">
                                            No responses yet
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mt: 0.5 }}
                                        >
                                            Submitted responses will appear
                                            here.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <TableContainer
                                        component={Paper}
                                        elevation={0}
                                        sx={{
                                            maxHeight:
                                                "calc(100vh - 300px)",
                                            overflow: "auto",
                                        }}
                                    >
                                        <Table
                                            stickyHeader
                                            size="small"
                                            aria-label="Survey responses"
                                            sx={{
                                                minWidth: tableMinWidth,
                                                "& .MuiTableCell-root":
                                                    {
                                                        px: {
                                                            xs: 1.5,
                                                            sm: 2,
                                                        },
                                                        py: 1.25,
                                                        verticalAlign:
                                                            "top",
                                                    },
                                            }}
                                        >
                                            <TableHead>
                                                <TableRow>
                                                    {columns.map(
                                                        (column) => {
                                                            const isFiltered =
                                                                filterValues.has(
                                                                    column.key,
                                                                );
                                                            return (
                                                                <TableCell
                                                                    key={
                                                                        column.key
                                                                    }
                                                                    sx={{
                                                                        minWidth:
                                                                            column.fixed
                                                                                ? 180
                                                                                : 230,
                                                                        maxWidth:
                                                                            column.fixed
                                                                                ? 220
                                                                                : 320,
                                                                        fontWeight: 800,
                                                                        color: "text.primary",
                                                                        backgroundColor:
                                                                            "background.subtle",
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                "flex",
                                                                            alignItems:
                                                                                "flex-start",
                                                                            gap: 0.5,
                                                                        }}
                                                                    >
                                                                        <TableSortLabel
                                                                            active={
                                                                                sortBy.key ===
                                                                                column.key
                                                                            }
                                                                            direction={
                                                                                sortBy.key ===
                                                                                column.key
                                                                                    ? sortBy.direction
                                                                                    : "asc"
                                                                            }
                                                                            onClick={() =>
                                                                                handleSort(
                                                                                    column.key,
                                                                                )
                                                                            }
                                                                            sx={{
                                                                                flex: 1,
                                                                                minWidth: 0,
                                                                                fontWeight: 800,
                                                                                "& .MuiTableSortLabel-icon":
                                                                                    {
                                                                                        color: "primary.main !important",
                                                                                    },
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                component="span"
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontWeight: 800,
                                                                                    whiteSpace:
                                                                                        "normal",
                                                                                    lineHeight: 1.35,
                                                                                }}
                                                                            >
                                                                                {
                                                                                    column.text
                                                                                }
                                                                            </Typography>
                                                                        </TableSortLabel>
                                                                        {!column.fixed && (
                                                                            <Tooltip
                                                                                title={`Filter ${column.text}`}
                                                                            >
                                                                                <IconButton
                                                                                    size="small"
                                                                                    color={
                                                                                        isFiltered
                                                                                            ? "primary"
                                                                                            : "default"
                                                                                    }
                                                                                    aria-label={`Filter ${column.text}`}
                                                                                    onClick={() =>
                                                                                        openFilterDialog(
                                                                                            column,
                                                                                        )
                                                                                    }
                                                                                    sx={{
                                                                                        mt: -0.5,
                                                                                        mr: -0.75,
                                                                                        backgroundColor:
                                                                                            isFiltered
                                                                                                ? "rgba(0, 149, 61, 0.09)"
                                                                                                : "transparent",
                                                                                    }}
                                                                                >
                                                                                    <FilterAltOutlinedIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        )}
                                                                    </Box>
                                                                </TableCell>
                                                            );
                                                        },
                                                    )}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {displayedRows.length ===
                                                0 ? (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={
                                                                columns.length
                                                            }
                                                            sx={{
                                                                py: "48px !important",
                                                                textAlign:
                                                                    "center",
                                                                color: "text.secondary",
                                                            }}
                                                        >
                                                            No responses match
                                                            the selected
                                                            filters.
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    displayedRows.map(
                                                        (row) => (
                                                            <TableRow
                                                                key={
                                                                    row.key
                                                                }
                                                                hover
                                                                sx={{
                                                                    "&:last-child td, &:last-child th":
                                                                        {
                                                                            borderBottom: 0,
                                                                        },
                                                                    "&:hover":
                                                                        {
                                                                            backgroundColor:
                                                                                "background.subtle",
                                                                        },
                                                                }}
                                                            >
                                                                <TableCell
                                                                    component="th"
                                                                    scope="row"
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    {
                                                                        row.user
                                                                    }
                                                                </TableCell>
                                                                <TableCell
                                                                    sx={{
                                                                        whiteSpace:
                                                                            "nowrap",
                                                                        color: "text.secondary",
                                                                    }}
                                                                >
                                                                    {formatSubmissionDate(
                                                                        row.submissionDate,
                                                                    )}
                                                                </TableCell>
                                                                {row.answers.map(
                                                                    (
                                                                        answer,
                                                                        index,
                                                                    ) => (
                                                                        <TableCell
                                                                            key={
                                                                                columns[
                                                                                    index +
                                                                                        2
                                                                                ]
                                                                                    ?.key ||
                                                                                index
                                                                            }
                                                                            sx={{
                                                                                maxWidth: 320,
                                                                                whiteSpace:
                                                                                    "pre-wrap",
                                                                                overflowWrap:
                                                                                    "anywhere",
                                                                            }}
                                                                        >
                                                                            {answer ||
                                                                                "—"}
                                                                        </TableCell>
                                                                    ),
                                                                )}
                                                            </TableRow>
                                                        ),
                                                    )
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Card>
                        </Box>
                    )}
                </Container>
            </Box>

            <Dialog
                open={filterDialogOpen}
                onClose={() => setFilterDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                aria-labelledby="survey-filter-dialog-title"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                            maxHeight: "90vh",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            borderRadius: 2,
                            color: "primary.main",
                            backgroundColor:
                                "rgba(0, 149, 61, 0.09)",
                        }}
                    >
                        <FilterAltOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="survey-filter-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Filter answers
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ mt: 0.5 }}
                        >
                            {filterColumn?.text}
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close filter"
                        onClick={() => setFilterDialogOpen(false)}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 1.5, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            borderRadius: 2.5,
                            borderColor:
                                "divider",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                px: 2,
                                py: 1.25,
                                backgroundColor: "background.subtle",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 800 }}
                            >
                                Included answers
                            </Typography>
                            <Button
                                size="small"
                                color="inherit"
                                onClick={() =>
                                    setDialogSelectedValues(
                                        new Set(dialogValues),
                                    )
                                }
                            >
                                Select all
                            </Button>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                maxHeight: 380,
                                overflowY: "auto",
                                px: 1.5,
                                py: 1,
                            }}
                        >
                            {dialogValues.map((value) => (
                                <FormControlLabel
                                    key={String(value)}
                                    control={
                                        <Checkbox
                                            checked={dialogSelectedValues.has(
                                                value,
                                            )}
                                            onChange={() =>
                                                toggleDialogValue(value)
                                            }
                                        />
                                    }
                                    label={
                                        value === ""
                                            ? "(Blank answer)"
                                            : String(value)
                                    }
                                    sx={{ mx: 0 }}
                                />
                            ))}
                        </Box>
                    </Paper>
                </DialogContent>

                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 1.5,
                        justifyContent: "space-between",
                    }}
                >
                    <Button
                        color="inherit"
                        onClick={clearCurrentFilter}
                    >
                        Clear filter
                    </Button>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                            color="inherit"
                            onClick={() =>
                                setFilterDialogOpen(false)
                            }
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={applyFilter}
                        >
                            Apply filter
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <ToolbarWithLogo />
        </>
    );
}
